import {
  VictoryBar,
  VictoryChart,
  VictoryAxis,
  VictoryContainer,
  VictoryTooltip,
  Background,
} from "victory"
import { ReactElement } from "react"
import { useTheme } from "@mui/material"
import { colors } from "../../../utils/theme"

const DEFAULT_CHART_PADDING = { top: 16, right: 16, bottom: 25, left: 19 }

interface BarChartProps<T extends object> {
  data: T[]
  xKey: keyof T
  yKey: keyof T
  xTickFormatter?: (value: string) => string
  yTickFormatter?: (value: number) => string
  yTicks?: number[]
  yAxisTickLabelComponent?: ReactElement
  getBarColor?: (datum: T) => string
  /**
   * When provided, hovering a bar shows a Victory tooltip with this label.
   * Return `undefined` to suppress the tooltip for a datum (e.g. missing values).
   * Use `\n` for a multi-line label (value + day).
   */
  getTooltipLabel?: (datum: T) => string | undefined
  maxY?: number
  chartPadding?: {
    top?: number
    right?: number
    bottom?: number
    left?: number
  }
  height?: number
  width?: number
  /**
   * When true, Victory scales the SVG to the parent width using `width`/`height`
   * as the aspect ratio. Prefer this inside fluid layouts to avoid overflow.
   */
  responsive?: boolean
  /** Bar thickness in chart coordinates. Defaults to 53. */
  barWidth?: number
  /** Top corner radius for bars. Defaults to 4. */
  cornerRadius?: number
}

/**
 * A generic bar chart component using Victory.
 * Tick labels follow the ambient MUI / Vira theme (light ink on dark).
 */
export default function BarChart<T extends object>({
  data,
  xKey,
  yKey,
  xTickFormatter,
  yTickFormatter,
  yTicks,
  yAxisTickLabelComponent,
  getBarColor,
  getTooltipLabel,
  maxY,
  height,
  width,
  responsive = false,
  barWidth = 53,
  cornerRadius = 4,
  chartPadding = DEFAULT_CHART_PADDING,
}: BarChartProps<T>) {
  const theme = useTheme()
  const isDark = theme.palette.mode === "dark"
  const tickFill =
    theme.palette.vira?.muted ??
    (isDark ? "#F0F3FA" : colors.safetyPlan.graphText)
  const chartBg = isDark ? theme.palette.background.paper : colors.neutral.white
  const gridStroke = isDark
    ? "rgba(255, 255, 255, 0.08)"
    : colors.opacities.black03

  // Match Summary footer / typeScale.meta — Victory SVG units read larger than CSS px.
  const axisTickLabelStyle = {
    fill: tickFill,
    fontFamily: "Lato, Karla, sans-serif",
    fontWeight: 400,
    lineHeight: 14,
    fontSize: 10,
    textAlign: "center" as const,
  }

  const tooltipFlyoutStyle = {
    fill: isDark ? theme.palette.vira?.softSurface : colors.neutral.space,
    stroke: "transparent",
  }

  const tooltipLabelStyle = {
    fill: isDark ? theme.palette.vira?.ink : colors.neutral.white,
    fontFamily: "Lato",
    fontSize: 11,
    fontWeight: 400,
  }

  return (
    <VictoryChart
      domain={maxY ? { y: [0, maxY] } : undefined}
      domainPadding={[20, 20]}
      padding={chartPadding}
      height={height}
      width={width}
      containerComponent={<VictoryContainer responsive={responsive} />}
      backgroundComponent={
        <Background
          style={{
            fill: chartBg,
          }}
        />
      }
    >
      <VictoryAxis
        dependentAxis
        tickValues={yTicks}
        tickFormat={yTickFormatter}
        tickLabelComponent={yAxisTickLabelComponent}
        style={{
          axis: {
            stroke: "transparent",
          },
          tickLabels: axisTickLabelStyle,
          grid: {
            stroke: gridStroke,
          },
        }}
      />
      <VictoryAxis
        tickFormat={xTickFormatter}
        style={{
          axis: {
            stroke: "transparent",
          },
          tickLabels: axisTickLabelStyle,
          grid: {
            stroke: "transparent",
          },
        }}
      />
      <VictoryBar
        data={data}
        x={(datum) => datum[xKey] as string | number}
        y={(datum) => datum[yKey] as number}
        barWidth={barWidth}
        cornerRadius={cornerRadius}
        labels={
          getTooltipLabel
            ? ({ datum }) => getTooltipLabel(datum as T) ?? ""
            : undefined
        }
        labelComponent={
          getTooltipLabel ? (
            <VictoryTooltip
              constrainToVisibleArea
              cornerRadius={4}
              flyoutStyle={tooltipFlyoutStyle}
              style={tooltipLabelStyle}
              flyoutPadding={{ top: 8, bottom: 8, left: 12, right: 12 }}
            />
          ) : undefined
        }
        style={{
          data: {
            fill: ({ datum }) =>
              getBarColor?.(datum as T) ?? colors.primary.periwinkle,
          },
        }}
      />
    </VictoryChart>
  )
}
